import GiftSvg from '@app/assets/svg/Gift';
import ROUTE from '@app/navigationApp/routes';
import RemoteImage from '@app/sharedComponents/RemoteImage/RemoteImage';
import { ThemedText, ThemedView } from '@app/theme';
import { trackEvent } from '@app/utils/amplitude';
import dayjs from 'dayjs';
import React from 'react';
import { Platform, Pressable, StyleSheet } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

interface BannerDetails {
    title: string;
    name: string;
    message: string;
    endDate: number;
}

interface ReferralBannerItem {
    referralParticipantType: 'REFERRER' | 'REFEREE';
    bannerType: 'STATIC' | 'DYNAMIC';
    bannerUrl: string;
    bannerDetails: BannerDetails | null;
}

interface Props {
    banners: ReferralBannerItem[];
    navigation?: any;
    sourcePage?: string;
    /** When set, called on mount instead of default HOME_* view events */
    onBannerView?: (banner: ReferralBannerItem, position: number) => void;
    onBannerClick?: (banner: ReferralBannerItem) => void;
}

/**
 * StaticBanner — for REFERRER participants with bannerType=STATIC.
 * Shows just the banner image full-width. Tapping navigates to Rewards.
 */
const StaticBanner: React.FC<{
    item: ReferralBannerItem;
    navigation?: any;
    position: number;
    sourcePage?: string;
    onBannerView?: (banner: ReferralBannerItem, position: number) => void;
    onBannerClick?: () => void;
}> = ({ item, navigation, position, sourcePage, onBannerView, onBannerClick }) => {
    React.useEffect(() => {
        if (onBannerView) {
            onBannerView(item, position);
        } else {
            trackEvent('HOME_REFERRAL_BANNER_VIEWED', {
                source_page: sourcePage ?? 'home_dashboard',
                banner_position: position,
            });
        }
    }, []);

    return (
        <Pressable onPress={() => { onBannerClick?.(); navigation?.navigate(ROUTE.REWARDS_SCREEN); }}>
            <ThemedView mt="lg" borderRadius="md" overflow="hidden" borderWidth={1} borderColor="border-primary">
                <RemoteImage
                    code={item.bannerUrl}
                    style={styles.staticImage}
                    resizeMode="cover"
                />
            </ThemedView>
        </Pressable>
    );
};

/**
 * DynamicBanner — for REFEREE participants with bannerType=DYNAMIC.
 * Linear gradient card with coins image, referrer info, and countdown (≤15 days).
 */
const DynamicBanner: React.FC<{
    item: ReferralBannerItem;
    position: number;
    sourcePage?: string;
    onBannerView?: (banner: ReferralBannerItem, position: number) => void;
}> = ({ item, position, sourcePage, onBannerView }) => {
    const { bannerDetails } = item;
    const [, setTick] = React.useState(0);

    React.useEffect(() => {
        const interval = setInterval(() => {
            setTick((prev) => prev + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    React.useEffect(() => {
        if (onBannerView) {
            onBannerView(item, position);
        } else {
            trackEvent('REFEREE_HOME_REFERRALBANNER_VIEWED', {
                source_page: sourcePage ?? 'home_dashboard',
                referrer_name: bannerDetails?.name ?? null,
                is_referred_user: true,
            });
        }
    }, []);

    if (!bannerDetails) return null;

    const now = dayjs();

    // MOCK FOR TESTING: fixed end date for 24 hours
    // To revert to backend date, use: const end = dayjs(bannerDetails.endDate);
    // const end = React.useMemo(() => dayjs().add(23, 'hour'), []);
    const end = dayjs(bannerDetails.endDate);

    const totalSecondsLeft = end.diff(now, 'second');
    const daysLeft = end.diff(now, 'day');

    // Show countdown when ≤ 15 days remain and time hasn't expired
    const showCountdown = daysLeft <= 15 && totalSecondsLeft > 0;

    let countdownNumber = '';
    let countdownLabel = '';

    const totalHoursLeft = Math.floor(totalSecondsLeft / 3600);
    const totalMinutesLeft = Math.floor(totalSecondsLeft / 60);

    if (daysLeft >= 1) {
        countdownNumber = String(daysLeft).padStart(2, '0');
        countdownLabel = `day${daysLeft === 1 ? '' : 's'} left`;
    } else if (totalHoursLeft >= 1) {
        countdownNumber = String(totalHoursLeft).padStart(2, '0');
        countdownLabel = `hour${totalHoursLeft === 1 ? '' : 's'} left`;
    } else {
        countdownNumber = String(totalMinutesLeft).padStart(2, '0');
        countdownLabel = `min${totalMinutesLeft === 1 ? '' : 's'} left`;
    }

    return (
        <ThemedView mt="lg" borderRadius="lg" overflow="hidden">
            <LinearGradient
                colors={['#FFFFFF', '#FFECAB']}
                start={{ x: 0, y: 0.5 }}
                end={{ x: 1, y: 0.5 }}
                style={styles.gradientCard}
            >
                {/* Coins image — top right */}
                {!!item.bannerUrl && (
                    <RemoteImage
                        code={item.bannerUrl}
                        style={styles.coinsImage}
                        resizeMode="cover"
                    />
                )}

                {/* Top row: gift icon + "Referred by Name" */}
                <ThemedView flexDirection="row" alignItems="center" mb="xs" style={styles.referrerRow}>
                    <GiftSvg />
                    <ThemedText variant="b4r" color="text-secondary" ml="xs">
                        {bannerDetails.title}{' '}
                        <ThemedText variant="b4s" color="text-primary">
                            {bannerDetails.name}
                        </ThemedText>
                    </ThemedText>
                </ThemedView>

                {/* Divider — gradient from #EBC067 to transparent */}
                <LinearGradient
                    colors={['#EBC067', '#EBC06700']}
                    start={{ x: 0, y: 0.5 }}
                    end={{ x: 1, y: 0.5 }}
                    style={styles.divider}
                />

                {/* Bottom row: optional countdown + message */}
                <ThemedView flexDirection="row" alignItems="center" pr="3xl">
                    {showCountdown && (
                        <ThemedView mr="md" alignItems="center" style={styles.countdownBlock}>
                            <ThemedText variant="h2s" color="text-primary" style={styles.countdownNumber}>
                                {countdownNumber}
                            </ThemedText>
                            <ThemedText variant="b4s" color="text-primary">
                                {countdownLabel}
                            </ThemedText>
                        </ThemedView>
                    )}
                    <ThemedView flex={1}>
                        <ThemedText variant="b4m" color="text-primary">
                            {bannerDetails.message}
                        </ThemedText>
                    </ThemedView>
                </ThemedView>
            </LinearGradient>
        </ThemedView>
    );
};

const ReferralBanner: React.FC<Props> = ({ banners, navigation, sourcePage, onBannerView, onBannerClick }) => {
    if (!banners || banners.length === 0) return null;

    return (
        <>
            {banners.map((banner, index) => {
                if (banner.bannerType === 'STATIC') {
                    return (
                        <StaticBanner
                            key={index}
                            item={banner}
                            navigation={navigation}
                            position={index}
                            sourcePage={sourcePage}
                            onBannerView={onBannerView}
                            onBannerClick={() => onBannerClick?.(banner)}
                        />
                    );
                }
                if (banner.bannerType === 'DYNAMIC') {
                    return (
                        <DynamicBanner
                            key={index}
                            item={banner}
                            position={index}
                            sourcePage={sourcePage}
                            onBannerView={onBannerView}
                        />
                    );
                }
                return null;
            })}
        </>
    );
};

const styles = StyleSheet.create({
    staticImage: {
      // width: '100%',
      // height: 100,
      aspectRatio: 4.5,
    },
    gradientCard: {
        borderRadius: 12,
        padding: 16,
        overflow: 'hidden',
        position: 'relative',
        minHeight: 110,
    },
    coinsImage: Platform.select({
        web: {
            position: 'absolute',
            right: 0,
            top: 0,
            width: 90,
            height: 90,
        },
        default: {
            position: 'absolute',
            right: 0,
            top: 0,
            width: 90,
            height: 90,
        },
    }),
    referrerRow: {
        paddingRight: 80, // make room for coins image
    },
    countdownBlock: {
        minWidth: 48,
    },
    countdownNumber: {
        lineHeight: 36,
    },
    divider: {
        height: 1,
        width: '70%',
        marginBottom: 8,
    },
});

export default ReferralBanner;
